import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowDownLeft,
  ArrowUpRight,
  Clock,
  ExternalLink,
  RefreshCw,
  Repeat,
  Search,
  XCircle,
} from 'lucide-react';
import { gauntlet } from '../lib/api';
import { InFlight } from '../components/InFlight';
import { chainColor, cx, formatAmount, formatDate, formatFiat, shortenAddress, timeAgo } from '../lib/format';
import { Badge, EmptyState, Input, Panel, Skeleton } from '../components/ui';

type Direction = 'all' | 'in' | 'out';

interface TransferRow {
  id: number;
  txid: string;
  direction: 'in' | 'out' | 'self';
  from_address: string | null;
  to_address: string | null;
  amount: string;
  fee: string | null;
  timestamp: number;
  symbol: string;
  decimals: number;
  chain_id: string;
  status: string;
  confirmations: number;
  is_internal: number;
  /** Fiat value of the movement in the user's currency, or null when the asset has no price. */
  fiat: string | null;
}

/**
 * Everything that has moved, in and out.
 *
 * Grouped by day, because "when did that arrive" is how people actually search
 * their history — not by scrolling an undifferentiated list.
 */
export function ActivityPage() {
  const [direction, setDirection] = useState<Direction>('all');
  const [search, setSearch] = useState('');
  const [chainFilter, setChainFilter] = useState<string>('all');

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['tx-history'],
    queryFn: () => gauntlet.txHistory(),
    refetchInterval: 60_000,
  });

  const { data: chainData } = useQuery({
    queryKey: ['chains'],
    queryFn: () => gauntlet.chains(),
    staleTime: Infinity,
  });

  const transfers = (data?.transfers ?? []) as unknown as TransferRow[];
  const currency = data?.currency ?? 'usd';

  const chainsPresent = useMemo(
    () => [...new Set(transfers.map((t) => t.chain_id))].sort(),
    [transfers],
  );

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return transfers.filter((t) => {
      if (direction !== 'all' && t.direction !== direction) return false;
      if (chainFilter !== 'all' && t.chain_id !== chainFilter) return false;
      if (!needle) return true;
      return (
        t.txid?.toLowerCase().includes(needle) ||
        t.from_address?.toLowerCase().includes(needle) ||
        t.to_address?.toLowerCase().includes(needle) ||
        t.symbol?.toLowerCase().includes(needle)
      );
    });
  }, [transfers, direction, chainFilter, search]);

  /** Group by calendar day, newest first. */
  const grouped = useMemo(() => {
    const groups = new Map<string, TransferRow[]>();
    for (const t of [...filtered].sort((a, b) => b.timestamp - a.timestamp)) {
      const day = new Date(t.timestamp).toDateString();
      const bucket = groups.get(day);
      if (bucket) bucket.push(t);
      else groups.set(day, [t]);
    }
    return [...groups.entries()];
  }, [filtered]);

  const explorerFor = (chainId: string, txid: string): string | null => {
    const chain = chainData?.chains.find((c) => c.id === chainId);
    return chain?.explorer ? chain.explorer.tx.replace('{tx}', txid) : null;
  };

  if (isLoading) return <Skeleton className="h-96 w-full" />;

  const totals = {
    in: transfers.filter((t) => t.direction === 'in').length,
    out: transfers.filter((t) => t.direction === 'out').length,
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Activity</h1>
          <p className="mt-1 text-sm text-ink-dim">
            Everything sent and received, across every chain in this wallet.
          </p>
        </div>
        <button
          onClick={() => void refetch()}
          className="inline-flex items-center gap-1.5 rounded-lg border border-hairline px-3 py-1.5 text-xs text-ink-dim transition-colors hover:text-ink"
        >
          <RefreshCw size={13} className={cx(isFetching && 'animate-spin')} />
          Refresh
        </button>
      </div>

      <InFlight />

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        <div className="relative min-w-52 flex-1">
          <Search
            size={15}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-faint"
          />
          <Input
            placeholder="Search address, transaction id or asset…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        <div className="flex gap-1.5">
          {(
            [
              ['all', `All (${transfers.length})`],
              ['in', `Received (${totals.in})`],
              ['out', `Sent (${totals.out})`],
            ] as const
          ).map(([id, label]) => (
            <button
              key={id}
              onClick={() => setDirection(id)}
              className={cx(
                'rounded-lg border px-3 py-1.5 text-xs transition-colors',
                direction === id
                  ? 'border-brass/50 bg-brass-wash text-brass'
                  : 'border-hairline text-ink-dim hover:text-ink',
              )}
            >
              {label}
            </button>
          ))}
        </div>

        {chainsPresent.length > 1 && (
          <select
            value={chainFilter}
            onChange={(e) => setChainFilter(e.target.value)}
            className="h-10 rounded-lg border border-hairline bg-abyss px-3 text-sm text-ink"
          >
            <option value="all">All chains</option>
            {chainsPresent.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        )}
      </div>

      {grouped.length === 0 ? (
        <Panel>
          <EmptyState
            icon={<Clock size={28} />}
            title={transfers.length === 0 ? 'Nothing yet' : 'Nothing matches those filters'}
            description={
              transfers.length === 0
                ? 'Once you send or receive something it will appear here. Press Sync to check the chains for activity.'
                : 'Try clearing the search or switching chain.'
            }
          />
        </Panel>
      ) : (
        grouped.map(([day, rows]) => (
          <div key={day} className="space-y-2">
            <p className="label-xs px-1">
              {day === new Date().toDateString() ? 'Today' : day}
            </p>
            <Panel padded={false}>
              <ul className="divide-y divide-hairline">
                {rows.map((t) => {
                  const incoming = t.direction === 'in';
                  const internal = t.is_internal === 1 || t.direction === 'self';
                  const counterparty = incoming ? t.from_address : t.to_address;
                  const url = explorerFor(t.chain_id, t.txid);

                  return (
                    <li key={t.id} className="flex items-center gap-3 px-4 py-3">
                      {/* Direction is the first thing to read, so it gets colour
                          and an arrow rather than only a text label. */}
                      <div
                        className={cx(
                          'flex size-9 shrink-0 items-center justify-center rounded-full border',
                          internal
                            ? 'border-hairline bg-raised text-ink-dim'
                            : incoming
                              ? 'border-gain/30 bg-gain/10 text-gain'
                              : 'border-loss/30 bg-loss/10 text-loss',
                        )}
                      >
                        {internal ? (
                          <Repeat size={15} />
                        ) : incoming ? (
                          <ArrowDownLeft size={16} />
                        ) : (
                          <ArrowUpRight size={16} />
                        )}
                      </div>

                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span className="text-sm font-medium">
                            {internal ? 'Moved between your accounts' : incoming ? 'Received' : 'Sent'}
                          </span>
                          {t.status === 'pending' && (
                            <Badge tone="warn">
                              <Clock size={9} className="mr-1 inline" />
                              pending
                            </Badge>
                          )}
                          {t.status === 'failed' && (
                            <Badge tone="loss">
                              <XCircle size={9} className="mr-1 inline" />
                              failed
                            </Badge>
                          )}
                          {internal && <Badge>not a disposal</Badge>}
                        </div>
                        <p className="addr mt-0.5 truncate text-ink-faint">
                          {counterparty
                            ? `${incoming ? 'from' : 'to'} ${shortenAddress(counterparty)}`
                            : t.txid
                              ? shortenAddress(t.txid, 10, 8)
                              : ''}
                          <span className="ml-1.5 not-italic">· {timeAgo(t.timestamp)}</span>
                        </p>
                      </div>

                      <div className="shrink-0 text-right">
                        <p
                          className={cx(
                            'num text-sm font-medium',
                            internal ? 'text-ink' : incoming ? 'text-gain' : 'text-ink',
                          )}
                        >
                          {internal ? '' : incoming ? '+' : '−'}
                          {formatAmount(t.amount, t.decimals ?? 8)} {t.symbol}
                        </p>
                        {/* Fiat at a glance, like Proton — the number people
                            actually read. Only when the asset has a price. */}
                        {t.fiat != null && (
                          <p className="num text-[11px] text-ink-faint">
                            {internal ? '' : incoming ? '+' : '−'}
                            {formatFiat(t.fiat, currency)}
                          </p>
                        )}
                        {t.fee && Number(t.fee) > 0 && !incoming && (
                          <p className="num text-[11px] text-ink-faint">
                            fee {formatAmount(t.fee, t.decimals ?? 8)}
                          </p>
                        )}
                      </div>

                      <div className="flex shrink-0 items-center gap-1.5">
                        <span
                          className="size-1.5 rounded-full"
                          style={{ background: chainColor(t.chain_id) }}
                          title={t.chain_id}
                        />
                        {url && (
                          <a
                            href={url}
                            target="_blank"
                            rel="noreferrer noopener"
                            className="rounded p-1 text-ink-faint transition-colors hover:text-brass"
                            aria-label="View on block explorer"
                            title={`View on explorer · ${formatDate(t.timestamp)}`}
                          >
                            <ExternalLink size={13} />
                          </a>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </Panel>
          </div>
        ))
      )}

      {transfers.length > 0 && (
        <p className="text-center text-xs text-ink-faint">
          Showing {filtered.length} of {transfers.length}. History is fetched from chain indexers on
          each sync.
        </p>
      )}
    </div>
  );
}
